export const COOKIE_CONSENT_KEY = 'ompli_cookie_consent';
export const COOKIE_CONSENT_EVENT = 'ompli:cookie-consent';
export const COOKIE_CONSENT_OPEN_EVENT = 'ompli:cookie-consent-open';

export type CookieConsentChoice = 'accepted' | 'necessary';

export type CookieConsentState = {
  choice: CookieConsentChoice;
  /** Optional / marketing cookies (e.g. live chat widgets). */
  analytics: boolean;
  updatedAt: string;
};

function isChoice(value: unknown): value is CookieConsentChoice {
  return value === 'accepted' || value === 'necessary';
}

export function readCookieConsent(): CookieConsentState | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsentState>;
    if (!isChoice(parsed.choice)) return null;
    return {
      choice: parsed.choice,
      analytics: Boolean(parsed.analytics),
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function hasCookieConsent(): boolean {
  return readCookieConsent() !== null;
}

export function hasAnalyticsConsent(): boolean {
  return Boolean(readCookieConsent()?.analytics);
}

export function writeCookieConsent(state: Omit<CookieConsentState, 'updatedAt'>): CookieConsentState {
  const next: CookieConsentState = {
    ...state,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(next));
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: next }));
  return next;
}

export function acceptAllCookies(): CookieConsentState {
  return writeCookieConsent({ choice: 'accepted', analytics: true });
}

export function acceptNecessaryCookies(): CookieConsentState {
  return writeCookieConsent({ choice: 'necessary', analytics: false });
}

export function saveCookiePreferences(analytics: boolean): CookieConsentState {
  return writeCookieConsent({
    choice: analytics ? 'accepted' : 'necessary',
    analytics,
  });
}

/** Re-open the cookie consent popup (e.g. footer Cookies link). */
export function openCookieConsent() {
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_OPEN_EVENT));
}
